// src/components/LetterheadPanel.js
//
// RULING 229 (2026-09-25). The General Chairperson creates his committee's letterhead - Bob: "offer the
// General Chairmen the opportunity to create their own letterhead". It is written to
// committees/{committee}.letterhead by the committee's own GC (self-scope, one field; firestore.rules),
// and every letter the platform generates for this committee prints it as a banner above the resolved
// office-of-record header (src\data\letterhead.js threads into src\data\letterGroup.js's header).
//
// Every rule of who-may and what-shape is src\data\letterhead.js (pure, tested); this draws and writes.
// Modelled on ContinuityPlanPanel (the other one-field GC self-update on the committee document).
//
// THE LOGO, honestly: `logoPath` is carried as a pointer, but the letters this platform produces are
// PLAIN TEXT and cannot embed an image, so a logo changes nothing a generated letter contains today.
// Uploading committee logo bytes to a committee-scoped Storage path is a named follow-on (ruling 232);
// until then this field is not offered for edit, so nothing implies a capability that is not built.
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import {
  mayRecordLetterhead, letterheadRecord, renderLetterheadBanner, hasLetterhead,
  MAX_ADDRESS_LINES, MAX_IDENTIFIERS,
} from '../data/letterhead';
import RulingRef from './RulingRef';

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

export default function LetterheadPanel({ user, committee }) {
  const [record, setRecord] = useState(undefined);
  const [form, setForm] = useState(null);   // null until the current value loads, so the editor seeds from it once
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  // Forensic audit 2026-10-16: true once he types and until he saves. While it is set nothing from the server
  // overwrites his form.
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!committee) return undefined;
    setRecord(undefined); setDirty(false);
    const unsub = onSnapshot(doc(db, 'committees', committee),
      s => { setRecord(s.exists() ? (s.data().letterhead || null) : null); setErr(''); },
      // Forensic audit 2026-10-16: a failed read is NOT "no letterhead" - the record is left as it was (unknown
      // until it loads), so the panel never says "not set" and never seeds an empty form to save over his letterhead.
      e => { setErr('Could not read the committee record: ' + (e && e.code ? e.code : e.message) + '. Whether this committee has a letterhead is unknown until it can be read.'); });
    return () => unsub();
  }, [committee]);

  // Forensic audit 2026-10-16: `record` is a new object on EVERY snapshot of the committee document, so any write to
  // that document (another field, another officer) used to re-seed the form and wipe his unsaved edits. The form now
  // follows the STORED VALUES (a string of them, which only changes when the letterhead itself does), and never while
  // he has unsaved edits.
  const storedKey = record === undefined ? undefined : JSON.stringify({
    committeeName: (record && record.committeeName) || '',
    addressLines: (record && Array.isArray(record.addressLines) ? record.addressLines.join('\n') : ''),
    identifiers: (record && Array.isArray(record.identifiers) ? record.identifiers.join('\n') : ''),
  });
  useEffect(() => {
    if (storedKey === undefined || dirty) return;
    setForm(JSON.parse(storedKey));
  }, [storedKey, dirty]);

  const may = mayRecordLetterhead(user, committee);
  const preview = useMemo(() => {
    if (!form) return '';
    return renderLetterheadBanner({
      committeeName: form.committeeName,
      addressLines: String(form.addressLines || '').split(/\r?\n/),
      identifiers: String(form.identifiers || '').split(/\r?\n/),
    });
  }, [form]);

  if (!committee) return null;

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); setSaved(false); setErrors([]); };
  const save = async () => {
    if (record === undefined) return;   // Forensic audit 2026-10-16: nothing is saved over a record nobody has read
    setErrors([]); setSaved(false);
    const r = letterheadRecord(user, committee, {
      committeeName: form.committeeName,
      addressLines: String(form.addressLines || '').split(/\r?\n/),
      identifiers: String(form.identifiers || '').split(/\r?\n/),
      logoPath: record && record.logoPath ? record.logoPath : null,   // preserved, not editable here (ruling 232)
    });
    if (!r.ok) { setErrors(r.errors); return; }
    setSaving(true);
    try { await updateDoc(doc(db, 'committees', committee), r.patch); setSaved(true); setDirty(false); }
    catch (e) { setErrors(['Not saved: ' + (e && e.code ? e.code : e.message)]); }
    setSaving(false);
  };

  return (
    <div style={{ border: '1px solid ' + (hasLetterhead(record) ? C.line : C.accent), background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 14px', borderBottom: '1px solid ' + C.line }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Committee letterhead - {committee}</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>Your committee's identity on every letter the platform generates for it - committee name, address, identifiers. It prints as a banner above the header. It is presentation only: it never signs a letter and never speaks for the office of record below it.<RulingRef user={user} n={236} why="A General Chairperson may create his committee's own letterhead; it is presentation and does not sign or attest anything." /></div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: hasLetterhead(record) ? C.ok : C.accent, whiteSpace: 'nowrap' }}>{record === undefined ? (err ? 'could not read' : '…') : hasLetterhead(record) ? 'set' : 'not set'}</div>
      </div>
      <div style={{ padding: '10px 14px 12px', fontSize: 12, color: C.dim }}>
        {err ? <div style={{ color: C.bad, marginBottom: 8 }}>&#9888; {err}</div> : null}
        {!may ? (
          <div style={{ color: C.text }}>
            {record === undefined ? (err ? null : '…')   /* Forensic audit 2026-10-16: unread is not "no letterhead" */
              : hasLetterhead(record)
              ? 'This committee has a letterhead. Only its General Chairperson can change it.'
              : 'This committee has no letterhead yet. Only its General Chairperson can set one.'}
          </div>
        ) : form ? (
          <div>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label className="form-label">Committee name (required)</label>
              <input aria-label="Committee name (required)" className="form-input" value={form.committeeName} placeholder="General Committee GO-342, SMART-TD" onChange={e => set('committeeName', e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label className="form-label">Address (one line each, up to {MAX_ADDRESS_LINES})</label>
              <textarea aria-label="Address (one line each, up to )" className="form-input" rows={3} value={form.addressLines} placeholder={'123 Rail Avenue\nSyracuse, NY 13201'} onChange={e => set('addressLines', e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label className="form-label">Identifiers (one line each, up to {MAX_IDENTIFIERS}) - optional</label>
              <textarea aria-label="Identifiers (one line each, up to ) - optional" className="form-input" rows={2} value={form.identifiers} placeholder={'GCA GO-342\nphone / e-mail as you want it to read'} onChange={e => set('identifiers', e.target.value)} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ color: C.dim, marginBottom: 4 }}>Preview (as it prints atop a letter):</div>
              <pre style={{ whiteSpace: 'pre-wrap', background: '#07101C', border: '1px solid ' + C.line, borderRadius: 6, padding: 10, color: C.text, minHeight: 20 }}>{preview || '(enter a committee name to see the banner)'}</pre>
            </div>
            {errors.length ? <div style={{ color: C.bad, marginBottom: 6 }}>{errors.map((e, i) => <div key={i}>&#9888; {e}</div>)}</div> : null}
            {dirty ? <span style={{ marginRight: 10, color: C.accent }}>Unsaved changes.</span> : null}
            <button className="btn btn-primary btn-sm" disabled={saving || !!err || record === undefined || !form.committeeName.trim()} onClick={save}>{saving ? 'Saving…' : 'Save letterhead'}</button>
            {saved ? <span style={{ marginLeft: 10, color: C.ok }}>Saved.</span> : null}
          </div>
        ) : (err ? null : <div>…</div>)}
      </div>
    </div>
  );
}
